import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { highlightSearchTerm } from '../../../components/custom_components/Search/Search'
import { LegalEntityTiny } from '../../../types'
import { Select, Spin } from 'antd'

const { Option } = Select

interface LegalEntitySearchSelectProps {
  searchTerm: string
  onSearch: (value: string) => void
  onClear: () => void
  legalEntities: LegalEntityTiny[]
  loading: boolean
  shouldShowOptions: boolean
  onOptionClick: (le: LegalEntityTiny) => void
  placeholder: string
  selectedLegalEntity: LegalEntityTiny | null
}

export const LegalEntitySearchSelect: FC<LegalEntitySearchSelectProps> = ({
  searchTerm,
  onSearch,
  onClear,
  legalEntities,
  loading,
  shouldShowOptions,
  onOptionClick,
  placeholder,
  selectedLegalEntity,
}) => {
  const { t } = useTranslation()

  // Render function for legal entity options
  const renderOptions = useCallback(
    (le: LegalEntityTiny) => (
      <Option key={`le-${le.id}`} value={le.id} label={le.name}>
        <div className="option" onClick={() => onOptionClick(le)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span className="option-text">{highlightSearchTerm(le.name, searchTerm)}</span>
          </div>
        </div>
      </Option>
    ),
    [onOptionClick, searchTerm],
  )

  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
          <Spin size="small" style={{ marginRight: '8px' }} />
          <span>{t('navigation.search.loading')}</span>
        </div>
      )
    } else if (searchTerm.length > 1) {
      return t('navigation.search.noResults')
    } else if (searchTerm.length > 0 && searchTerm.length <= 1) {
      return t('navigation.search.startTyping')
    }
    return t('navigation.search.startTyping')
  }, [searchTerm, loading, t])

  return (
    <Select
      showSearch
      placeholder={placeholder}
      value={
        selectedLegalEntity
          ? {
              key: selectedLegalEntity.id,
              label: selectedLegalEntity.name,
            }
          : undefined
      }
      labelInValue
      onSearch={onSearch}
      onChange={(value) => {
        if (value && typeof value === 'object' && value.key) {
          const le = legalEntities.find((l) => l.id === value.key)
          if (le) {
            onOptionClick(le)
          }
        } else {
          onClear()
        }
      }}
      allowClear
      filterOption={false}
      loading={loading}
      notFoundContent={notFoundContent}
      style={{ width: '100%' }}
      size="small"
      dropdownMatchSelectWidth={false}
    >
      {shouldShowOptions &&
        legalEntities
          .filter((le) => {
            if (selectedLegalEntity && le.id === selectedLegalEntity.id) {
              return false
            }
            return true
          })
          .map(renderOptions)}
    </Select>
  )
}
